import { CubeSide } from "@aec-craft/nb-generative";
import { BimType, DefaultRegionType } from "@aec-craft/nb-generative-core";
import { ExportStatus } from "@be/z/z-export";
import { Lang } from "./lang";

export type Translations = Readonly<typeof en>;

const mappingCategoryLabelsEn: Record<string, string> = {
	walls: "Walls",
	doors: "Doors",
	windows: "Windows",
	floors: "Floors",
	roof: "Roof",
};

const mappingSlotLabelsEn: Record<string, string> = {
	"wall:exterior": "Exterior wall",
	"wall:facade": "Facade wall",
	"wall:partition": "Partition wall",
	door: "Door",
	window: "Window",
	"floor:structural": "Structural floor",
	"floor:finish": "Finish floor",
	roof: "Roof",
};

const mappingCategoryLabelsDe: Record<string, string> = {
	walls: "Wände",
	doors: "Türen",
	windows: "Fenster",
	floors: "Böden",
	roof: "Dach",
};

const mappingSlotLabelsDe: Record<string, string> = {
	"wall:exterior": "Außenwand",
	"wall:facade": "Fassadenwand",
	"wall:partition": "Trennwand",
	door: "Tür",
	window: "Fenster",
	"floor:structural": "Tragende Decke",
	"floor:finish": "Bodenbelag",
	roof: "Dach",
};

const en = {
	bimTypes: {
		[BimType.world]: "World",
		[BimType.site]: "Site",
		[BimType.plot]: "Plot",
		[BimType.building]: "Building",
		[BimType.wing]: "Wing",
		[BimType.load]: "Load",
		[BimType.level]: "Level",
		[BimType.corridor]: "Corridor",
		[BimType.roof]: "Roof",
		[BimType.commercialSpace]: "Commercial Section",
		[BimType.apartment]: "Apartment",
		[BimType.core]: "Core",
		[BimType.wall]: "Facade walls",
		[BimType.door]: "Door",
		[BimType.window]: "Window",
		[BimType.opening]: "Opening",
		[BimType.floor]: "Floor",
		[BimType.ceiling]: "Ceiling",
		[BimType.room]: "Room",
		[BimType.box]: "Box",
		[BimType.massingConfig]: "Massing Config",
		[BimType.wingConfig]: "Wing Config",
		[BimType.extrusion2D]: "Extrusion 2D",
		[BimType.genericTechnicalSpace]: "Generic Technical Section",
		[BimType.railing]: "Railing",
		[BimType.parkingSpot]: "Parking Spot",
		[BimType.meta]: "Meta",
		[BimType.stairs]: "Stairs",
		[BimType.region]: "Region",
		[BimType.path]: "Path",
		[BimType.lineParking]: "Line parking",
	},
	regionTypes: {
		[DefaultRegionType.Parking]: "Parking",
		[DefaultRegionType.GreenSpace]: "Green space",
		[DefaultRegionType.PavedArea]: "Paved area",
		[DefaultRegionType.Recreation]: "Recreation",
		[DefaultRegionType.ServicesUtilities]: "Services/utilities",
	},
	cubeFaces: {
		[CubeSide.top]: "TOP",
		[CubeSide.bottom]: "BOTTOM",
		[CubeSide.left]: "LEFT",
		[CubeSide.right]: "RIGHT",
		[CubeSide.front]: "FRONT",
		[CubeSide.back]: "BACK",
	},
	displaySettings: {
		mapViewMode: "Map View Mode",
		noMap: "No Map",
		map2D: "2D Map",
		map2DOsmBuildings: "2D Map + OSM Buildings",
		map2DBasemapDe: "2D Map + basemap.de LoD2",
		map3D: "3D Map",
		camera: "Camera",
		orthographic: "Orthographic",
		perspective: "Perspective",
		display: "Display",
		referenceImage: "Reference Image",
		uploadImage: "Upload Image",
		removeImage: "Remove",
		opacity: "Opacity",
		outline: "Outline",
		ambientOcclusion: "Ambient occlusion",
	},
	navigation: {
		infoMode: "Info mode - view building information",
		settingsTooltip: "Settings",
		generationTooltip: "Generation",
		visibilityTooltip: "Visibility",
		sunTooltip: "Sun position",
		elementsTooltip: "Elements",
		valuationTooltip: "Valuation",
		enhanceImageTooltip: "Enhance Image",
		exportTooltip: "Export",
		variantsTooltip: "Variants",
		variantsPanelTitle: "Variants",
		variantsPanelDescription:
			"Compare and manage design options by efficiency, coverage, and GFA.",
		metricsTooltip: "Metrics",
		metricsPanelTitle: "Metrics",
		metricsPanelDescription:
			"View project metrics including areas, ratios, and unit mix.",
		chatTooltip: "Chat",
		chatPanelTitle: "Chat",
		chatPanelDescription: "Ask questions about your project",
		paragraph34Tooltip: "§34 German Building Code",
		paragraph34PanelTitle: "§34 German Building Code",
		paragraph34PanelDescription:
			"Neighbourhood context targets (first site only)",
		referenceImagesTooltip: "Reference Images",
		reportTooltip: "Report",
		warningsTooltip: "Warnings",
		warningsPanelTitle: "Warnings",
		warningsPanelDescription: "Validation warnings for the current layout",
		elements: "Elements",
		variants: (layout: string) => `Derived from ${layout}`,
		layouts: "Layouts",
		sortBy: "Sort by",
	},
	tools: {
		moreTools: "More tools",
		viewOnlyMode: "View only mode",
		editMode: "Edit mode",
		commentMode: "Comment mode",
		selectParcelAsSite: "Select parcel as a new site",
		drawNewSite: "Draw a new site",
		cutSites: "Cut sites",
		unionSites: "Union sites",
		deleteSite: "Delete site",
		editSite: "Edit site",
		editSiteOffset: "Edit site offset",
		editSiteSetback: "Edit Site Setbacks",
		addNonBuildableArea: "Add no-build region",
		deleteNonBuildableArea: "Delete no-build region",
		addPath: "Add path",
		deletePath: "Delete path",
		pathWidth: "Width",
		addLineParking: "Add line parking",
		deleteLineParking: "Delete line parking",
		lineParkingStallWidth: "Stall width",
		lineParkingStallDepth: "Stall depth",
		lineParkingAccessDepth: "Access depth",
		lineParkingType: "Type",
		lineParkingTilt: "Tilt",
		lineParkingFlipSide: "Flip side",
		regionFill: "Fill",
		regionType: "Type",
		regionAffectsBuildingSetback: "Affects building setback",
		unknownRegionType: "Unknown",
		addCustomRegionType: "Add custom region type",
		customRegionTypePlaceholder: "New region type",
		addNewBuilding: "Add new building",
		undo: "Undo",
		enhancementStarted: "Image enhancement started",
		enhancementComplete: "Image enhancement complete",
		redo: "Redo",
		deleteBuilding: {
			enabled: "Delete building",
			disabled: "Only saved buildings can be deleted",
		},
		moveRotateBuilding: "Move/Rotate Building",
		changeWingsLength: "Change Wings Length",
		addNewWings: {
			enabled: "Add new wings",
			disabled: "Wings cannot be added to this shape of building",
		},
		editLevels: "Edit Levels",
		editWingLevels: "Edit Wing Levels",
		flipLoad: {
			enabled: "Flip Load",
			disabled: "Only single-load buildings can be load-flipped",
		},
		floorplanEditor: "Floorplan editor",
		autoRotate: "Auto rotate camera",
		focusCamera: "Focus camera",
		orientateCamera: "Orientate camera",
		captureCurrentView: "Capture Current View",
		captureAndEnhance: "Capture and Enhance",
		enhanceImage: "Enhance Image",
		imageHistory: "Image History",
		clear: "Clear",
		openInNewTab: "Open in new tab",
		downloadImage: "Download image",
		enhancing: "Enhancing",
		enhanced: "Enhanced",
		original: "Original",
		failed: "Failed",
		loadMore: "Load More",
		deleteImage: "Delete image",
		generateVideo: "Generate Video",
		viewVideo: "View Video",
		enhancementOptions: "Enhancement Options",
		solarPanels: "Solar Panels",
		landscaping: "Landscaping",
		customEnhancement: "Custom Enhancement",
		customPromptPlaceholder: "Describe how it should look...",
		generationSteps: {
			enhance: "Enhancing",
			customPrompt: "Adding solar panels and landscaping",
		},
		stepMayTakeMinutes: "This step may take a few minutes",
		cancelOperation: "Cancel operation",
		finishOperation: "Finish operation",
		measureDistance: "Measure",
	},
	errors: {
		canvasNotFound: "Canvas not found. Please ensure the 3D view is loaded.",
		imageEnhancementFailed: "Failed to enhance image.",
		imageDeleteFailed: "Failed to delete image. Please try again.",
	},
	feedback: {
		tooltip: "Feedback",
		title: "Give us your feedback",
		description:
			"Are you experiencing any errors? Have a feature request? Send us a message and a member of the team will contact you.",
		message: "Message",
		send: "Send",
		sending: "Sending",
		cancel: "Cancel",
		error: "There was an error sending your message, please try again later.",
		success: "Your message has been successfully sent.",
		ok: "Ok",
	},
	contact: {
		title: "Contact us",
		description:
			"If you have any questions, feedback or need assistance, please reach out to us.",
		message: "Message",
		send: "Send",
		sending: "Sending",
		cancel: "Cancel",
		error: "There was an error sending your message, please try again later.",
		success: "Message successfully sent, one of our team will be in touch",
		ok: "Ok",
	},
	chatPanel: {
		newChat: "New Chat",
		deleteChat: "Delete chat",
	},
	buildingSettings: {
		title: "Building Settings",
		levels: "Levels",
		global: "Global",
		building: "Building",
	},
	generationPanel: {
		generation: "Generation",
		target: "Target",
		wingLengthRange: "Length Range",
		wingLengthDescription: "Minimum and maximum length for building wings",
		wingMinLength: "Min. Length",
		wingMinLengthDescription: "Minimum length for building wings",
		wingMaxLength: "Max. Length",
		wingMaxLengthDescription: "Maximum length for building wings",
		maxFragmentLength: "Max. Section Length",
		maxFragmentLengthDescription: "Maximum length of a building section",
		wingMinSetback: "Min. Setback",
		wingMinSetbackDescription:
			"Minimum reserved distance on each building side",
		wingSetbackFactor: "Setback Factor",
		wingSetbackFactorDescription:
			"Multiplied by building height to calculate setback distance",
		balconiesAffectMassing: "Balconies Affect Massing",
		balconiesAffectMassingDescription:
			"Reserve the balcony-inflated setback during generation instead of the base setback",
		corridorWidth: "Corridor Width",
		corridorWidthDescription: "Default corridor width for all buildings",
		globalBuildingHeight: "Max Levels",
		levels: "levels",
		globalBuildingHeightDescription:
			"Maximum number of levels for all generated buildings",
		unitHeight: "Height",
		unitHeightDescription: "Unit height for residential units",
		unitDepth: "Depth",
		unitDepthDescription: "Unit depth for residential units",
		unitMix: "Unit Mix",
		unitMixEnabled: "Enabled",
		resetToEqualDistribution: "Reset to Equal Distribution",
		commercialSection: "Commercial Section",
		commercialSectionDescription:
			"Adds a commercial section on the ground level with the specified height",
		commercialHeight: "Commercial Height",
		site: "Site",
		targetLandCoverage: "Max GSI",
		targetLandCoverageDescription:
			"Maximum percentage of the plot area that buildings may cover.",
		maxAncillaryBcr: "Max GSI II",
		maxAncillaryBcrDescription:
			"Maximum percentage of the plot area covered by buildings plus sealed ancillary surfaces: parking, paved and utility regions, and paths. Turned off, the legal default applies: Max GSI × 1.5, capped at 0.8. Requires Max GSI — without a main coverage limit there is nothing to exceed.",
		farValue: "Max FAR",
		farValueDescription:
			"Target floor area ratio. When the limit is on, levels are added until the target is reached — bounded by Max Levels. Turn it off to remove the FAR cap.",
		noLimit: "No limit",
		loadType: "Load Type",
		loadTypeDouble: "Double",
		loadTypeSingle: "Single",
		buildingGroup: "Building",
		wingsGroup: "Wings",
		unitsGroup: "Units",
		bimObjects: "BIM Objects",
		bimObjectThicknessDescription:
			"Thickness in metres used when generating this element. Kept in sync with the mapped Revit family size.",
		parkingGroup: "Parking",
		parkingFactor: "Parking factor",
		parkingFactorDescription:
			"Enter the number of car parking spaces per residential unit, calculated by multiplying the local parking ratio factor with any applicable zone or mobility concept factor. Please check your municipality's regulations to determine the correct values for your project.",
		requiredParkingSpots: "Required parking spots",
		requiredParkingSpotsDescription:
			"Total apartments multiplied by the parking factor.",
		apartmentTypes: "Apartment Types",
		newType: "New Type",
		name: "Name",
		rooms: "Rooms",
		inhabitants: "Inhabitants",
		width: "Width",
		depth: "Depth",
		cancel: "Cancel",
		save: "Save",
	},
	targets: {
		suggested: "§34 German Building Code",
		useSuggested: "use suggested",
	},
	hierarchyPanel: {
		site: "Site",
		building: "Building",
	},
	visibilityPanel: {
		elementVisibility: "Element Visibility",
		site: "Site",
		outerBuilding: "Building",
		units: "Units",
		unitElements: "Unit Elements",
		resetToDefaults: "Reset to Defaults",
		buildingSetbacks: "Building Setbacks",
		plotSetbacks: "Site Setbacks",
		warnings: "Warnings",
		logo: "Organisation Logo",
		streets: "Streets",
		general: "General",
		parapet: "Parapet",
		facade: "Facade",
		structuralFloors: "Structural Floors",
		finishFloors: "Finish Floors",
	},
	sunPanel: {
		resetToDefaults: "Reset to Defaults",
		shadows: "Shadows",
		date: "Date",
		time: "Time",
		sunHoursAnalysis: "Sun Hours Analysis",
		daylightPotential: "Daylight Potential",
		hours: "Hours",
		percentOfSky: "% of Sky",
		analysisFailed: "Analysis failed. Please try again.",
		total: "Total",
		facade: "Facade",
		roof: "Roof",
		ground: "Ground",
	},
	valuationPanel: {
		valuation: "Valuation",
		total: "Total",
		view: "View",
		project: "Project",
		statistics: "Statistics",
		livingArea: "Living Area",
		facadeArea: "Facade Area",
		windowArea: "Window Area",
		buildingYear: "Year of Completion",
		buildingsYear: "Year of Completion",
		defaultBuildingYear: "Default Year",
		buildingYearHelperDefault: "Default year of completion for new buildings",
		buildingYearPastIndicator: "Past year",
		buildingYearPresentIndicator: "Present year",
		residentialUnits: "Residential Units",
		buildings: "Buildings",
		constructionCost: "Construction Cost",
		totalCost: "Total Cost",
		marketValue: "Market Value",
		price: "Price",
		salePrice: "Sale price",
		priceRange: "Price Range",
		priceRangeMin: "Price Range Min",
		priceRangeMax: "Price Range Max",
		rentPrice: "Rent Price",
		netRent: "Net Rent",
		rentRange: "Rent Range",
		rentRangeMin: "Rent Range Min",
		rentRangeMax: "Rent Range Max",
		valuationUpdated: "Valuation updated:",
		confidence: "Confidence:",
		// New Valuation Section translations
		projectGeometry: "Project Geometry",
		residentialArea: "Residential Area",
		residentialAreaHelper: "Living area (net floor area)",
		grossFloorArea: "Gross Floor Area",
		grossFloorAreaHelper: "Total floor area including walls",
		footprint: "Building Footprint",
		footprintHelper: "Ground floor area covered by building",
		apartments: "Apartments",
		levels: "Levels",
		constructionCosts: "Construction Costs",
		basebuild: "Base Build",
		electrical: "Electrical",
		hvac: "HVAC",
		hvacFullName: "Heating, Ventilation & Air Conditioning",
		shellConstruction: "Shell Construction",
		siteSetup: "Site Setup",
		drainage: "Drainage",
		facade: "Facade",
		costsPerSqmResidential: "Costs per m² (Residential Area)",
		ancillaryCosts: "Ancillary Building Costs",
		costsPerItem: "Costs per Item",
		lift: "Elevator",
		doors: "Doors",
		bathrooms: "Bathrooms",
		balcony: "Balcony",
		parking: "Parking",
		costsPerSqmFootprint: "Costs per m² (Footprint)",
		roof: "Roof",
		cellar: "Cellar",
		totalConstructionCost: "Total Construction Cost",
		totalProjectCosts: "Total Project Costs",
		landCostPerSqm: "Land Cost per m²",
		landCostTotal: "Total Land Cost",
		landCostTotalHelper: "Edit total to back-calculate per m² price",
		sideCosts: "Side Costs",
		sideCostsHelper: "Permits, fees, etc. (% of construction cost)",
		totalCostsPreFinance: "Total Costs (Pre-Finance)",
		financing: "Financing",
		buildTime: "Build Time",
		equityShare: "Equity Share",
		loanInterestRate: "Loan Interest Rate",
		loanShare: "Loan Share",
		equity: "Equity",
		loan: "Loan",
		totalInterestCost: "Total Interest Cost",
		totalInterestCostHelper:
			"Progressive financing - funds drawn evenly over build period. Interest calculated on average outstanding balance (50% of total loan)",
		totalProjectCostAllIn: "Total Project Cost (All-in)",
		revenueAssumptions: "Revenue Assumptions",
		salesPricePerSqm: "Sales Price per m²",
		salesPriceOverride: "Override Price",
		totalSalesValue: "Total Sales Value",
		summaryResults: "Summary & Results",
		netProfit: "Net Profit",
		equityInvested: "Equity Invested",
		returnOnEquity: "Return on Equity (ROE)",
		returnOnEquityHelper: "Net profit / Equity invested",
		returnOnCost: "Return on Cost (ROC)",
		returnOnCostHelper: "Net profit / Total project cost",
	},
	metricsPanel: {
		projectOverview: "Site Metrics",
		projectOverviewDescription:
			"Basic project-wide values for land and apartment count.",
		buildingArea: "Building Area",
		buildingAreaDescription:
			"Total built-up, footprint, and usable internal space.",
		developmentRatios: "Development Ratios",
		developmentRatiosDescription:
			"Key performance indicators derived from the building layout.",
		regionAreasDescription:
			"Total no-build area and breakdown by type. Each type shows its share of the site and total designated no-build area. Overlapping types can total more than 100%.",
		noBuildRegions: "No-Build Regions",
		area: "Area",
		totalRegionArea: "Total Designated Area",
		siteAreaFraction: "Site Area Fraction",
		shareOfTotalDesignatedArea: "Share of Total Designated Area",
		noWorldLoaded: "No world loaded.",
		unitMixDistribution: "Unit Mix Distribution",
		unitMixDistributionDescription:
			"Breakdown of unit types by percentage and quantity.",
		siteArea: "Site Area",
		siteAreaDescription: "Total site area",
		apartments: "Apartments",
		apartmentsDescription: "Total number of apartments",
		buildingsDescription: "Total number of buildings",
		buildingFootprint: "Building Footprint",
		buildingFootprintDescription: "Total building footprint area",
		layoutEfficiency: "Layout Efficiency",
		layoutEfficiencyDescription:
			"Ratio of net usable area to gross floor area (includes corridors, walls, shafts)",
		buildingCoverageRatio: "Building Coverage Ratio (BCR)",
		buildingCoverageRatioDescription:
			"Ratio of building footprint to site area",
		ancillaryBuildingCoverageRatio: "Ancillary Coverage Ratio (GRZ II)",
		ancillaryBuildingCoverageRatioDescription:
			"Ratio of building footprint plus sealed ancillary ground to site area",
		grossFloorArea: "Gross Floor Area (GFA)",
		grossFloorAreaDescription:
			"Total floor area of all floors (includes walls, corridors, shafts)",
		netInternalArea: "Net Internal Area (NIA)",
		netInternalAreaDescription:
			"Total usable floor area (excludes walls, corridors, shafts)",
		livingArea: "Living Area (WoFlV)",
		livingAreaDescription:
			"Living area per German Wohnflächenverordnung (WoFlV §4): apartment floor area at 100 %, balconies at 25 %. No ceiling-height reductions applied.",
		floorAreaRatio: "Floor Area Ratio (FAR)",
		floorAreaRatioDescription: "Ratio of gross floor area to site area",
		areaToVolumeRatio: "Area-to-Volume Ratio (AVR)",
		areaToVolumeRatioDescription:
			"Ratio of building envelope area to building volume",
		unitMixDescription: "Apartment count by bedrooms and inhabitants",
		layoutEfficiencyPercent: "Layout Efficiency (%)",
		layoutEfficiencyTooltip:
			"Layout Efficiency: Ratio of net usable area to gross floor area.\n\nNet Internal Area (NIA): Total usable floor area (excludes walls, corridors, shafts)\n\nGross Floor Area (GFA): Total floor area of all floors (includes walls, corridors, shafts)",
		lotCoverageRatioPercent: "Lot Coverage Ratio (%)",
		lotCoverageRatioTooltip:
			"Lot Coverage Ratio: Percentage of the lot covered by buildings.\n\nBuilding Footprint: Total area covered by buildings at ground level\n\nSite Area: Total area of the development site",
		buildingFootprintSqm: "Building Footprint (m²)",
		buildingFootprintTooltip: "Total area covered by buildings at ground level",
		grossFloorAreaSqm: "Gross Floor Area (m²)",
		grossFloorAreaTooltip:
			"Total floor area of all floors (includes walls, corridors, shafts)",
		floorAreaRatioPercent: "Floor Area Ratio (%)",
		floorAreaRatioTooltip:
			"Floor Area Ratio: Ratio of total building floor area to site area.\n\nGross Floor Area (GFA): Total floor area of all floors\n\nSite Area: Total area of the development site",
		areaToVolumeRatioTooltip:
			"Ratio of building envelope area to building volume",
		variantCount: "Variant count",
	},
	paragraph34Panel: {
		far: "FAR",
		gsi: "GSI",
		height: "Height",
		noData: "No data. Ensure a site and parcels are loaded.",
		loadingBuildings: "Loading buildings…",
		withinLimits: "within limits",
		parcels: "Parcels",
		parcel: "Parcel",
		referenceLabel: "Reference",
		selectParcelHint: "Click a parcel to use it as the reference.",
		pickOnMap: "Pick on map",
		pickOnMapCancel: "Cancel",
		pickOnMapHint:
			"Click any parcel in the scene to use it as the reference. Hover to preview its values.",
		manualReferenceInfo:
			"is the reference — its FAR, GSI and height set the §34 limits and suggested targets.",
		resetReference: "Reset to auto",
		legendTitle: "Color legend",
		legendInRange: "At or below ref — within §34 limits",
		legendAboveMax: "Above ref — exceeds reference parcel",
		infoTitle: "How §34 targets are computed",
		infoNeighbours:
			"Neighbours: cadastral parcels whose outline is within 200 m of your site's parcel outline define the context.",
		infoFilter:
			"Filtering: empty parcels (no buildings) and statistical outliers on FAR and height are dropped.",
		infoReference:
			"Reference parcel: among the remaining neighbours, the one with the highest FAR. Its FAR, GSI and height set the Ref (ceiling).",
		infoCompliance:
			"Your project is within §34 limits when each metric stays at or below Ref.",
	},
	report: {
		title: "Project Report",
		noBuildings: "No buildings in this layout",
		noBuildingsDescription:
			"A report can't be created without units. Generate a building first.",
		generating: "Generating report...",
		reportDate: "Report date",
		developmentSummary: "Development summary",
		basis: "Basis",
		basisNote:
			"Figures are generated from the active layout and the valuation assumptions set in the project.",
		siteAndDesignMetrics: "Site and Design Metrics",
		siteAndDesignMetricsSubtitle:
			"What the site allows, what the design delivers, and how efficiently the built area is used.",
		costAndRevenueModel: "Cost and revenue model",
		costAndRevenueModelSubtitle:
			"Construction cost build-up, project costs, financing, and the resulting return.",
		feasibilityReport: "Feasibility report",
		feasibilityStudy: "Feasibility study",
		coverSubtitle:
			"Potential analysis for the development of high-quality residential space in an urban setting.",
		massingTopView: "Site massing — top view",
		estimate: "Estimate",
		range: "Range",
		perSqmLivingArea: "Per m² living area",
		monthly: "monthly",
		subtotalPreFinance: "Subtotal before financing",
		priceSourceMarket: "market estimate",
		priceSourceManual: "manual",
		page: "Page",
		printReport: "Print report",
		units: "Units",
		share: "Portion",
		region: "Region",
		area: "Area",
		ofSite: "Of site",
		shareOfDesignatedArea: "Share of Designated Area",
		item: "Cost group / item",
		rate: "Rate / Unit",
		amount: "Total",
		unitApartment: "unit",
		unitLevel: "level",
		unitYears: "y",
		unitFootprint: "m² FP",
		disclaimer: "Disclaimer & Reliance",
		disclaimerBasisTitle: "Data and methodology basis",
		disclaimerBasis1:
			"This feasibility study was prepared to the best of our knowledge and belief, and with due diligence. All analyses, assessments, key figures, forecasts, and conclusions are based on the information available at the time of preparation, in particular on documents and information provided by the client, publicly accessible sources, and – where applicable – on data-driven evaluations using software solutions and artificial intelligence (AI) methods.",
		disclaimerBasis2:
			"A comprehensive, independent review of all underlying assumptions, data sources, and other factors, in the sense of a technical, economic, tax, or legal due diligence, was neither required nor conducted. Only a careful plausibility check was performed within the framework of a preliminary, indicative analysis.",
		disclaimerWarrantyTitle: "Warranty disclaimer",
		disclaimerWarranty1:
			"Despite careful consideration, no guarantee can be given for the completeness, accuracy, and timeliness of the data used, the underlying assumptions, and the results derived therefrom. This applies in particular given that (i) parts of the content are based on probabilistic and algorithmic methods, including AI-supported models, which are subject to inherent uncertainties and sources of error, and (ii) framework conditions (e.g., market, cost, interest rate, legal, or funding environments) can change at any time.",
		disclaimerWarranty2:
			"The statements contained in this feasibility study – in particular regarding costs, deadlines, revenues, profitability, risks or feasibility – therefore represent non-binding assessments at the time of preparation and are expressly not to be understood as a promise, guarantee or agreement as to quality.",
		disclaimerUseTitle: "Intended Use & Liability",
		disclaimerUse1:
			"The feasibility study serves solely as a guide and preparation for further considerations by the client. It does not replace in-depth technical, legal, tax, or economic expert advice, nor does it replace detailed project planning. The client remains obligated to make independent decisions based on their own review and, where necessary, supplementary expert opinions and consulting services. Any direct or indirect use of the results for investment, financing, procurement, or acquisition decisions is at the client's own risk.",
		disclaimerUse2:
			'Any liability of the contractor for the accuracy, completeness, and timeliness of the feasibility study, its underlying assumptions, and for decisions or omissions made therefrom by the client or third parties is excluded to the extent permitted by law. In particular, claims arising from so-called "reliance" (dependence on the content of this study), including any indirect or consequential damages, are excluded. The foregoing limitations of liability apply equally to all contractual, pre-contractual, and extra-contractual claims and also extend to the benefit of the contractor\'s legal representatives, employees, and other agents.',
	},
	statusMessages: {
		siteSaved: "Site saved",
		siteNotSaved: "Site not saved",
		buildingSaved: "Building saved",
		buildingNotSaved: "Building not saved",
		saving: "Saving...",
		savedToCloud: "Saved to cloud",
	},
	buildingInfo: {
		residential: "Residential",
		units: "units",
	},
	validation: {
		buildingExceedsPlot: "Building exceeds plot",
		buildingSetbacksOverlap: "Building setbacks overlap",
		buildingSetbackExceedsSiteLimit:
			"Building setback exceeds the site setback limit",
		apartmentFacesPureNorth: "Apartments face pure north",
		siteExceedsMaxBcr: "Coverage exceeds the max building coverage ratio (BCR)",
		siteExceedsMaxAncillaryBcr:
			"Coverage incl. sealed surfaces exceeds the ancillary coverage allowance (GRZ II)",
		coreExceedsMaxApartments: "Core serves too many apartments",
	},
	warningsPanel: {
		noWarnings: "No warnings",
		archived: "Archived",
		archive: "Archive",
		restore: "Restore",
		showDetails: "Show details",
		hideDetails: "Hide details",
		moreDetails: (count: number) => `+${count} more`,
		warningTypes: "Warning types",
		warningsWhileEditing: "Warnings while editing",
	},
	userMenu: {
		name: "Name",
		color: "Color",
		language: "Language",
		theme: "Theme",
		themeSystem: "System",
		themeLight: "Light",
		themeDark: "Dark",
		organisation: "Organisation",
		manageAccount: "Manage account",
		manageOrganisation: "Manage organisation",
		apartmentSets: "Apartment sets",
		billing: "Billing",
		appAdmin: "App admin",
		contactUs: "Contact us",
		signOut: "Sign out",
		signIn: "Sign in",
		legal: "Legal",
		privacyPolicy: "Privacy Policy",
		cookiePolicy: "Cookie Policy",
		impressum: "Impressum",
		terms: "Terms and conditions",
	},
	locationPicker: {
		cannotUseThisLocation: "Cannot use this location",
		useThisLocation: "Use this location",
	},
	exportPanel: {
		generate: "Generate",
		exportType: "Export type",
		generateRevit: "Generate Revit file",
		generateIfc: "Generate IFC file",
		revitJobLabel: "Revit export",
		ifcJobLabel: "IFC export",
		generating: "Generating...",
		[ExportStatus.pending]: "Pending",
		[ExportStatus.inprogress]: "In progress",
		[ExportStatus.success]: "Complete",
		failed: "Failed",
		download: "Download file",
		downloadReport: "Download report",
		delete: "Delete",
		startNewJob: "Start new job",
		rawData: "Raw Data",
		export: "Export",
		revitFiles: "Revit files",
		uploadRevitFile: "Upload Revit file",
		uploading: "Uploading...",
		revitFileTooLarge: "File exceeds the size limit",
		revitFileExists:
			"A file with this name already exists — delete it first or rename the file",
		revitFileHint: "Revit 2025 reference project",
		referenceFile: "Reference file",
		defaultReferenceFile: "Default (built-in)",
		notMapped: "(not mapped)",
		noBuildings: "Add at least one building to export",
		extracting: "Extracting...",
		extractionFailed: "Extraction failed",
		mapFamilies: "Map families",
		downloadIfc: "Download IFC",
		mappingTitle: "Map families",
		mappingDescription:
			"Choose which family to use for each element type when exporting",
		mappingNone: "—",
		mappingRequired: "Map the families for this file before exporting",
		mappingOutOfSync:
			"BIM Object properties changed — update the family mapping first",
		mappingSearchPlaceholder: "Search families...",
		mappingNoResults: "No families found",
		mappingFixedSize: "(fixed size)",
		mappingCategoryLabels: mappingCategoryLabelsEn,
		mappingSlotLabels: mappingSlotLabelsEn,
		mappingLoading: "Loading families...",
		mappingLoadFailed: "Could not load the extracted families",
		mappingSaveFailed: "Could not save the mapping",
		mappingSave: "Save",
		mappingSaving: "Saving...",
		mappingCancel: "Cancel",
		applySizesTitle: "Update BIM Object properties?",
		applySizesDescription:
			"The mapped families have different sizes than the current generation settings. Apply them to this layout and regenerate?",
		applySizesApply: "Apply & regenerate",
		applySizesKeep: "Keep current values",
		revitFileUploadFailed:
			"Uploading the file to storage failed — please try again",
	},
	org: {
		apartmentSets: "Apartment Sets",
		globalApartmentSet: "Built-in",
		globalApartmentSetDescription:
			"Provided by gen.os.build and copied into every new project. Add your own apartment sets to customise.",
		defaultApartmentSet: "New projects default",
		defaultApartmentSetDescription:
			"New projects start with this apartment set selected.",
		setDefaultApartmentSet: "Set as new projects default",
		createApartmentSet: "New Apartment Set",
		createApartmentGroup: "New Apartment Group",
		createApartmentTemplate: "New Apartment Template",
		bulkUploadApartmentTemplates: "Bulk upload",
		bulkUploadApartmentTemplatesDescription:
			"Upload several apartment template JSON files at once. Each one is added to the group named in the file, creating that group when it doesn't exist yet.",
		bulkUploadReadyCount: (count: number) =>
			`${count} apartment template${count === 1 ? "" : "s"} ready to upload`,
		bulkUploadSkippedCount: (count: number) =>
			`${count} file${count === 1 ? "" : "s"} skipped — not a valid apartment template`,
		bulkUploadProgress: (uploaded: number, total: number) =>
			`Uploaded ${uploaded} of ${total}`,
		bulkUploadFailedCount: (count: number) =>
			`${count} upload${count === 1 ? "" : "s"} failed`,
		upload: "Upload",
		close: "Close",
		name: "Name",
		color: "Color",
		shuffleColor: "Shuffle color",
		create: "Create",
		cancel: "Cancel",
		remove: "Remove",
		update: "Update",
		apartmentGroup: "Apartment group",
		newApartmentGroup: "New group…",
		addApartmentTemplate: "Add apartment template",
		chooseApartmentGroup: "Choose which group this apartment belongs to.",
		add: "Add",
		removeApartmentSet: "Remove apartment set",
		removeApartmentSetDescription:
			"This apartment set and all of its groups and templates will be permanently removed.",
		removeApartmentTemplate: "Remove apartment template",
		removeApartmentTemplateDescription:
			"This apartment template will be permanently removed.",
	},
	appAdmin: {
		defaultApartmentSets: "Default Apartment Sets",
	},
	errorMessages: {
		whoops: "Whoops!",
		goToProjects: "Go to Projects",
		errors: {
			generic: "Something went wrong.",
			[401]: "You are not authorized to view this page.",
			[403]: "You do not have permission to access this page.",
			[404]: "The page you are looking for does not exist.",
			[410]: "The page you are looking for has been permanently removed.",
			[429]: "You have made too many requests. Please try again later.",
			[500]: "Something went wrong.",
			[503]: "Something went wrong.",
		},
		organizationError: {
			message: "You must be part of an organisation to use this app",
			action: "Please contact the admin or ",
			link: "request a demo",
		},
	},
} satisfies Record<string, unknown> & {
	bimTypes: Record<BimType, string>;
	cubeFaces: Record<CubeSide, string>;
	errorMessages: {
		whoops: string;
		goToProjects: string;
		errors: Record<string | number, string>;
		organizationError: {
			message: string;
			action: string;
			link: string;
		};
	};
};

export const translations: Record<Lang, Translations> = {
	[Lang.en]: en,
	[Lang.de]: {
		bimTypes: {
			[BimType.world]: "Welt",
			[BimType.site]: "Standort",
			[BimType.plot]: "Grundstück",
			[BimType.building]: "Gebäude",
			[BimType.wing]: "Flügel",
			[BimType.load]: "Last",
			[BimType.level]: "Ebene",
			[BimType.corridor]: "Flur",
			[BimType.roof]: "Dach",
			[BimType.commercialSpace]: "Gewerbebereich",
			[BimType.apartment]: "Wohnung",
			[BimType.core]: "Kern",
			[BimType.wall]: "Fassadenwände",
			[BimType.door]: "Tür",
			[BimType.window]: "Fenster",
			[BimType.opening]: "Eröffnung",
			[BimType.floor]: "Boden",
			[BimType.ceiling]: "Decke",
			[BimType.room]: "Raum",
			[BimType.box]: "Box",
			[BimType.massingConfig]: "Massenplanung",
			[BimType.wingConfig]: "Flügelkonfiguration",
			[BimType.extrusion2D]: "2D-Extrusion",
			[BimType.genericTechnicalSpace]: "Generischer Technikbereich",
			[BimType.railing]: "Geländer",
			[BimType.parkingSpot]: "Stellplatz",
			[BimType.meta]: "Meta",
			[BimType.stairs]: "Treppen",
			[BimType.region]: "Bereich",
			[BimType.path]: "Weg",
			[BimType.lineParking]: "Längsparkplatz",
		},
		regionTypes: {
			[DefaultRegionType.Parking]: "Parkplatz",
			[DefaultRegionType.GreenSpace]: "Grünfläche",
			[DefaultRegionType.PavedArea]: "Befestigte Fläche",
			[DefaultRegionType.Recreation]: "Freizeit",
			[DefaultRegionType.ServicesUtilities]: "Versorgung/Technik",
		},
		cubeFaces: {
			[CubeSide.top]: "OBEN",
			[CubeSide.bottom]: "UNTEN",
			[CubeSide.left]: "LINKS",
			[CubeSide.right]: "RECHTS",
			[CubeSide.front]: "VORNE",
			[CubeSide.back]: "HINTEN",
		},
		displaySettings: {
			mapViewMode: "Kartenansichtsmodus",
			noMap: "Keine Karte",
			map2D: "2D-Karte",
			map2DOsmBuildings: "2D-Karte + OSM-Gebäude",
			map2DBasemapDe: "2D-Karte + basemap.de LoD2",
			map3D: "3D-Karte",
			camera: "Kamera",
			orthographic: "Parallel",
			perspective: "Perspektivisch",
			display: "Anzeige",
			referenceImage: "Referenzbild",
			uploadImage: "Bild hochladen",
			removeImage: "Entfernen",
			opacity: "Deckkraft",
			outline: "Umriss",
			ambientOcclusion: "Umgebungsverdeckung",
		},
		navigation: {
			infoMode: "Info-Modus - Gebäudedaten anzeigen",
			settingsTooltip: "Einstellungen",
			generationTooltip: "Generierung",
			visibilityTooltip: "Sichtbarkeit",
			sunTooltip: "Sonnenposition",
			elementsTooltip: "Elemente",
			valuationTooltip: "Bewertung",
			enhanceImageTooltip: "Bild verbessern",
			exportTooltip: "Exportieren",
			variantsTooltip: "Varianten",
			variantsPanelTitle: "Varianten-Panel",
			variantsPanelDescription:
				"Designoptionen nach Effizienz, Abdeckung und GFA vergleichen und verwalten.",
			metricsTooltip: "Metriken",
			metricsPanelTitle: "Metriken",
			metricsPanelDescription:
				"Projektmetriken einschließlich Flächen, Verhältnisse und Einheitenverteilung anzeigen.",
			chatTooltip: "Chat",
			chatPanelTitle: "Chat",
			chatPanelDescription: "Stellen Sie Fragen zu Ihrem Projekt",
			paragraph34Tooltip: "§34 BauGB",
			paragraph34PanelTitle: "§34 BauGB",
			paragraph34PanelDescription:
				"Nachbarschaftliche Richtwerte (nur erstes Grundstück)",
			referenceImagesTooltip: "Referenzbilder",
			reportTooltip: "Bericht",
			warningsTooltip: "Warnungen",
			warningsPanelTitle: "Warnungen",
			warningsPanelDescription: "Validierungswarnungen des aktuellen Layouts",
			elements: "Elemente",
			variants: (layout: string) => `Von ${layout} abgeleitet`,
			layouts: "Layouts",
			sortBy: "Sortieren nach",
		},
		tools: {
			moreTools: "Weitere Werkzeuge",
			viewOnlyMode: "Ansichtsmodus",
			editMode: "Bearbeitungsmodus",
			commentMode: "Kommentarmodus",
			selectParcelAsSite: "Parzelle als neues Grundstück auswählen",
			drawNewSite: "Neues Grundstück zeichnen",
			cutSites: "Grundstücke schneiden",
			unionSites: "Grundstücke vereinen",
			deleteSite: "Grundstück löschen",
			editSite: "Grundstück bearbeiten",
			editSiteOffset: "Geländeversatz bearbeiten",
			editSiteSetback: "Standortabstände bearbeiten",
			addNonBuildableArea: "Bauverbotsbereich hinzufügen",
			deleteNonBuildableArea: "Bauverbotsbereich löschen",
			addPath: "Weg hinzufügen",
			deletePath: "Weg löschen",
			pathWidth: "Breite",
			addLineParking: "Längsparkplatz hinzufügen",
			deleteLineParking: "Längsparkplatz löschen",
			lineParkingStallWidth: "Stellplatzbreite",
			lineParkingStallDepth: "Stellplatztiefe",
			lineParkingAccessDepth: "Fahrgassentiefe",
			lineParkingType: "Typ",
			lineParkingTilt: "Neigung",
			lineParkingFlipSide: "Seite wechseln",
			regionFill: "Füllung",
			regionType: "Bereichstyp",
			regionAffectsBuildingSetback: "Bereich beeinflusst Gebäudeabstand",
			unknownRegionType: "Unbekannt",
			addCustomRegionType: "Benutzerdefinierten Bereichstyp hinzufügen",
			customRegionTypePlaceholder: "Benutzerdefinierter Typname",
			addNewBuilding: "Neues Gebäude hinzufügen",
			undo: "Rückgängig",
			enhancementStarted: "Bildverbesserung gestartet",
			enhancementComplete: "Bildverbesserung abgeschlossen",
			redo: "Wiederholen",
			deleteBuilding: {
				enabled: "Gebäude löschen",
				disabled: "Nur gespeicherte Gebäude können gelöscht werden",
			},
			moveRotateBuilding: "Gebäude verschieben/drehen",
			changeWingsLength: "Flügellänge ändern",
			addNewWings: {
				enabled: "Neue Flügel hinzufügen",
				disabled:
					"Flügel können zu dieser Gebäudeform nicht hinzugefügt werden",
			},
			editLevels: "Geschosse bearbeiten",
			editWingLevels: "Geschoss pro Flügel bearbeiten",
			flipLoad: {
				enabled: "Erschließung wenden",
				disabled: "Nur Laubengang kann die Richtung flippen",
			},
			floorplanEditor: "Grundriss-Editor",
			autoRotate: "Kamera automatisch drehen",
			focusCamera: "Kamera zentrieren",
			orientateCamera: "Kamera ausrichten",
			captureCurrentView: "Aktuelle Ansicht aufnehmen",
			captureAndEnhance: "Aufnehmen und Verbessern",
			enhanceImage: "Bild verbessern",
			imageHistory: "Bildverlauf",
			clear: "Löschen",
			openInNewTab: "In neuem Tab öffnen",
			downloadImage: "Bild herunterladen",
			enhancing: "Wird verbessert",
			enhanced: "Verbessert",
			original: "Original",
			failed: "Fehlgeschlagen",
			loadMore: "Mehr laden",
			deleteImage: "Bild löschen",
			generateVideo: "Video generieren",
			viewVideo: "Video ansehen",
			enhancementOptions: "Verbesserungsoptionen",
			solarPanels: "Solarpaneele",
			landscaping: "Landschaftsgestaltung",
			customEnhancement: "Benutzerdefinierte Verbesserung",
			customPromptPlaceholder: "Beschreiben Sie, wie es aussehen soll...",
			generationSteps: {
				enhance: "Wird verbessert",
				customPrompt: "Solarpaneele und Landschaft werden hinzugefügt",
			},
			stepMayTakeMinutes: "Dieser Schritt kann einige Minuten dauern",
			cancelOperation: "Abbrechen",
			finishOperation: "Beenden",
			measureDistance: "Messen",
		},
		errors: {
			canvasNotFound:
				"Canvas nicht gefunden. Bitte stellen Sie sicher, dass die 3D-Ansicht geladen ist.",
			imageEnhancementFailed: "Bildverbesserung fehlgeschlagen.",
			imageDeleteFailed:
				"Bild konnte nicht gelöscht werden. Bitte versuchen Sie es erneut.",
		},
		feedback: {
			tooltip: "Feedback",
			title: "Gib uns dein Feedback",
			description:
				"Gab es Fehlermeldungen? Hast du Feature-Requests? Sende uns eine Nachricht und wir melden uns bei dir.",
			message: "Nachricht",
			send: "Abschicken",
			sending: "Wird gesendet",
			cancel: "Abbrechen",
			error:
				"Es gab Probleme beim senden deiner Nachricht, bitte versuche es später noch einmal.",
			success: "Deine Nachricht wurde erfolgreich gesendet.",
			ok: "Ok",
		},
		contact: {
			title: "Kontakt",
			description:
				"Wenn Sie Fragen oder Feedback haben oder Unterstützung benötigen, kontaktieren Sie uns bitte.",
			message: "Nachricht",
			send: "Abschicken",
			sending: "Wird gesendet",
			cancel: "Abbrechen",
			error:
				"Es gab Probleme beim senden deiner Nachricht, bitte versuche es später noch einmal.",
			success: "Deine Nachricht wurde erfolgreich gesendet.",
			ok: "Ok",
		},
		chatPanel: {
			newChat: "Neuer Chat",
			deleteChat: "Chat löschen",
		},
		buildingSettings: {
			title: "Gebäudeeinstellungen",
			levels: "Geschosse",
			global: "Global",
			building: "Gebäude",
		},
		generationPanel: {
			generation: "Generierung",
			target: "Ziel",
			wingLengthRange: "Längenbereich",
			wingLengthDescription: "Minimale und maximale Länge für Gebäudeflügel",
			wingMinLength: "Min. Länge",
			wingMinLengthDescription: "Minimale Länge für Gebäudeflügel",
			wingMaxLength: "Max. Länge",
			wingMaxLengthDescription: "Maximale Länge für Gebäudeflügel",
			maxFragmentLength: "Max. Gebäudeabschnitt",
			maxFragmentLengthDescription: "Maximale Länge eines Gebäudeabschnitts",
			wingMinSetback: "Min. Abstand",
			wingMinSetbackDescription:
				"Mindestens reservierter Abstand auf jeder Gebäudeseite",
			wingSetbackFactor: "Abstandsfaktor",
			wingSetbackFactorDescription:
				"Wird mit der Gebäudehöhe multipliziert, um den Abstand zu berechnen",
			balconiesAffectMassing: "Balkone beeinflussen die Baumasse",
			balconiesAffectMassingDescription:
				"Bei der Generierung den durch Balkone vergrößerten Abstand reservieren statt des Basisabstands",
			corridorWidth: "Flurbreite",
			corridorWidthDescription: "Standard-Flurbreite für alle Gebäude",
			globalBuildingHeight: "Max. Geschosse",
			levels: "Geschosse",
			globalBuildingHeightDescription:
				"Maximale Anzahl der Geschosse für alle generierten Gebäude",
			unitHeight: "Höhe",
			unitHeightDescription: "Einheitshöhe für Wohneinheiten",
			unitDepth: "Tiefe",
			unitDepthDescription: "Einheitentiefe für Wohneinheiten",
			unitMix: "Wohnungsmix",
			unitMixEnabled: "Aktiviert",
			resetToEqualDistribution: "Gleichmäßig verteilen",
			commercialSection: "Gewerbebereich",
			commercialSectionDescription:
				"Fügt einen Gewerbebereich im Erdgeschoss mit der angegebenen Höhe hinzu",
			commercialHeight: "Gewerbehöhe",
			site: "Grundstück",
			targetLandCoverage: "Max. GRZ",
			targetLandCoverageDescription:
				"Maximaler Anteil der Grundstücksfläche, der bebaut werden darf.",
			maxAncillaryBcr: "Max. GRZ II",
			maxAncillaryBcrDescription:
				"Maximaler Anteil der Grundstücksfläche, der durch Gebäude und versiegelte Nebenanlagen — Stellplätze, befestigte Flächen, Versorgungsflächen und Wege — bedeckt werden darf. Ausgeschaltet gilt die gesetzliche Regel: Max. GRZ × 1,5, höchstens 0,8. Erfordert Max. GRZ — ohne Hauptlimit gibt es nichts zu überschreiten.",
			farValue: "Max-GFZ",
			farValueDescription:
				"Ziel-Geschossflächenzahl. Bei aktiviertem Limit werden Geschosse hinzugefügt, bis der Zielwert erreicht ist – begrenzt durch die maximale Geschosszahl. Ausgeschaltet gilt kein GFZ-Limit.",
			noLimit: "Kein Limit",
			loadType: "Erschließungstyp",
			loadTypeDouble: "Flur",
			loadTypeSingle: "Laubengang",
			buildingGroup: "Gebäude",
			wingsGroup: "Flügel",
			unitsGroup: "Einheiten",
			bimObjects: "BIM-Objekte",
			bimObjectThicknessDescription:
				"Dicke in Metern, die bei der Generierung dieses Elements verwendet wird. Wird mit der Größe der zugeordneten Revit-Familie synchronisiert.",
			parkingGroup: "Parken",
			parkingFactor: "Stellplatzfaktor",
			parkingFactorDescription:
				"Geben Sie die Anzahl der Kfz-Stellplätze pro Wohneinheit ein, berechnet durch Multiplikation des lokalen Stellplatzschlüssels mit einem etwaigen Zonen- oder Mobilitätskonzeptfaktor. Bitte prüfen Sie die Vorschriften Ihrer Gemeinde, um die korrekten Werte für Ihr Projekt zu ermitteln.",
			requiredParkingSpots: "Benötigte Stellplätze",
			requiredParkingSpotsDescription:
				"Gesamtzahl der Wohnungen multipliziert mit dem Stellplatzfaktor.",
			apartmentTypes: "Wohnungstypen",
			newType: "Neuer Typ",
			name: "Name",
			rooms: "Zimmer",
			inhabitants: "Einwohner",
			width: "Breite",
			depth: "Tiefe",
			cancel: "Abbrechen",
			save: "Speichern",
		},
		targets: {
			suggested: "§34 BauGB",
			useSuggested: "Vorschlag übernehmen",
		},
		hierarchyPanel: {
			site: "Grundstück",
			building: "Gebäude",
		},
		visibilityPanel: {
			elementVisibility: "Element-Sichtbarkeit",
			site: "Grundstück",
			outerBuilding: "Gebäude",
			units: "Einheiten",
			unitElements: "Einheitselemente",
			resetToDefaults: "Auf Standardwerte zurücksetzen",
			buildingSetbacks: "Gebäudeabstände",
			plotSetbacks: "Grundstücksabstände",
			warnings: "Warnungen",
			logo: "Logo der Organisation",
			streets: "Straßen",
			general: "Allgemein",
			parapet: "Brüstung",
			facade: "Fassade",
			structuralFloors: "Strukturelle Böden",
			finishFloors: "Fertigböden",
		},
		sunPanel: {
			resetToDefaults: "Auf Standardwerte zurücksetzen",
			date: "Datum",
			time: "Uhrzeit",
			shadows: "Schatten",
			sunHoursAnalysis: "Sonnenstunden-Analyse",
			daylightPotential: "Tageslichtpotenzial",
			hours: "Stunden",
			percentOfSky: "% Himmel",
			analysisFailed: "Analyse fehlgeschlagen. Bitte erneut versuchen.",
			total: "Gesamt",
			facade: "Fassade",
			roof: "Dach",
			ground: "Boden",
		},
		valuationPanel: {
			valuation: "Bewertung",
			total: "Gesamt",
			view: "Ansicht",
			project: "Projekt",
			statistics: "Statistiken",
			livingArea: "Wohnfläche",
			facadeArea: "Fassadenfläche",
			windowArea: "Fensterfläche",
			buildingYear: "Baujahr",
			buildingsYear: "Baujahr",
			defaultBuildingYear: "Standardbaujahr",
			buildingYearHelperDefault: "Standardbaujahr für neue Gebäude",
			buildingYearPastIndicator: "Vergangenes Jahr",
			buildingYearPresentIndicator: "Aktuelles Jahr",
			residentialUnits: "Wohneinheiten",
			buildings: "Gebäude",
			constructionCost: "Baukosten",
			totalCost: "Gesamtkosten",
			marketValue: "Marktwert",
			price: "Preis",
			salePrice: "Verkaufspreis",
			priceRange: "Preisspanne",
			priceRangeMin: "Preisspanne Min",
			priceRangeMax: "Preisspanne Max",
			rentPrice: "Mietpreis",
			netRent: "Nettomiete",
			rentRange: "Mietpreisspanne",
			rentRangeMin: "Mietpreisspanne Min",
			rentRangeMax: "Mietpreisspanne Max",
			valuationUpdated: "Bewertung aktualisiert:",
			confidence: "Vertrauen:",
			// New Valuation Section translations
			projectGeometry: "Projektgeometrie",
			residentialArea: "Wohnfläche (Wfl)",
			residentialAreaHelper: "Wohnfläche - Wohnbereich",
			grossFloorArea: "Brutto-Grundfläche (BGF)",
			grossFloorAreaHelper: "Brutto-Grundfläche - Gesamtfläche",
			footprint: "Grundfläche",
			footprintHelper: "Gebäudegrundfläche",
			apartments: "Wohnungen",
			levels: "Ebenen",
			constructionCosts: "Baukosten",
			basebuild: "Rohbau",
			electrical: "Elektrik",
			hvac: "HLS",
			hvacFullName: "Heizung, Lüftung, Sanitär",
			shellConstruction: "Rohbau",
			siteSetup: "Baustelleneinrichtung",
			drainage: "Entwässerung",
			facade: "Fassade",
			costsPerSqmResidential: "Kosten pro m² (Wohnfläche)",
			ancillaryCosts: "Baunebenkosten (BNK)",
			costsPerItem: "Kosten pro Einheit",
			lift: "Aufzug",
			doors: "Türen",
			bathrooms: "Bäder",
			balcony: "Balkon",
			parking: "Stellplätze",
			costsPerSqmFootprint: "Kosten pro m² (Grundfläche)",
			roof: "Dach",
			cellar: "Keller",
			totalConstructionCost: "Gesamte Baukosten",
			totalProjectCosts: "Gesamte Projektkosten",
			landCostPerSqm: "Grundstückskosten pro m²",
			landCostTotal: "Gesamte Grundstückskosten",
			landCostTotalHelper:
				"Gesamtbetrag bearbeiten, um Preis pro m² zu berechnen",
			sideCosts: "Nebenkosten",
			sideCostsHelper: "Genehmigungen, Gebühren, etc. (% der Baukosten)",
			totalCostsPreFinance: "Gesamtkosten (vor Finanzierung)",
			financing: "Finanzierung",
			buildTime: "Bauzeit",
			equityShare: "Eigenkapitalanteil",
			loanInterestRate: "Darlehenszins",
			loanShare: "Darlehensanteil",
			equity: "Eigenkapital",
			loan: "Darlehen",
			totalInterestCost: "Gesamte Zinskosten",
			totalInterestCostHelper:
				"Progressive Finanzierung - Mittel werden gleichmäßig über die Bauzeit abgerufen. Zinsen auf durchschnittlichen Kreditsaldo berechnet (50% des Gesamtkredits)",
			totalProjectCostAllIn: "Gesamte Projektkosten (All-in)",
			revenueAssumptions: "Erlösannahmen",
			salesPricePerSqm: "Verkaufspreis pro m²",
			salesPriceOverride: "Preis überschreiben",
			totalSalesValue: "Gesamter Verkaufswert",
			summaryResults: "Zusammenfassung & Ergebnisse",
			netProfit: "Nettogewinn",
			equityInvested: "Investiertes Eigenkapital",
			returnOnEquity: "Eigenkapitalrendite (ROE)",
			returnOnEquityHelper: "Nettogewinn / Investiertes Eigenkapital",
			returnOnCost: "Kostenrendite (ROC)",
			returnOnCostHelper: "Nettogewinn / Gesamtprojektkosten",
		},
		metricsPanel: {
			projectOverview: "Standortmetriken",
			projectOverviewDescription:
				"Grundlegende projektweite Werte für Grundstück und Wohnungsanzahl.",
			buildingArea: "Gebäudefläche",
			buildingAreaDescription:
				"Gesamte Bebauung, Grundfläche und nutzbare Innenfläche.",
			developmentRatios: "Entwicklungsverhältnisse",
			developmentRatiosDescription:
				"Leistungskennzahlen abgeleitet aus dem Gebäudelayout.",
			regionAreasDescription:
				"Gesamtfläche der Bauverbotsbereiche und Aufschlüsselung nach Typ. Jeder Typ zeigt seinen Anteil am Grundstück und an der gesamten ausgewiesenen Fläche. Überlappende Typen können zusammen mehr als 100 % ergeben.",
			noBuildRegions: "Bauverbotsbereiche",
			area: "Fläche",
			totalRegionArea: "Gesamte ausgewiesene Fläche",
			siteAreaFraction: "Grundstücksflächenanteil",
			shareOfTotalDesignatedArea: "Anteil an der gesamten ausgewiesenen Fläche",
			noWorldLoaded: "Keine Welt geladen.",
			unitMixDistribution: "Wohnungsmix-Verteilung",
			unitMixDistributionDescription:
				"Aufschlüsselung der Wohnungstypen nach Prozentsatz und Anzahl.",
			siteArea: "Grundstücksfläche",
			siteAreaDescription: "Gesamtfläche des Grundstücks",
			apartments: "Wohnungen",
			apartmentsDescription: "Gesamtanzahl der Wohnungen",
			buildingsDescription: "Gesamtanzahl der Gebäude",
			buildingFootprint: "Gebäudegrundfläche",
			buildingFootprintDescription: "Gesamte Gebäudegrundfläche",
			layoutEfficiency: "Layout-Effizienz",
			layoutEfficiencyDescription:
				"Verhältnis der Netto-Nutzfläche zur Brutto-Grundfläche (inkl. Flure, Wände, Schächte)",
			buildingCoverageRatio: "Grundflächenzahl (GFZ)",
			buildingCoverageRatioDescription:
				"Verhältnis der Gebäudegrundfläche zur Grundstücksfläche",
			ancillaryBuildingCoverageRatio: "Grundflächenzahl II (GRZ II)",
			ancillaryBuildingCoverageRatioDescription:
				"Verhältnis der Gebäudegrundfläche plus versiegelter Nebenanlagen zur Grundstücksfläche",
			grossFloorArea: "Brutto-Grundfläche (BGF)",
			grossFloorAreaDescription:
				"Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			netInternalArea: "Netto-Nutzfläche (NNF)",
			netInternalAreaDescription:
				"Gesamte nutzbare Fläche innerhalb der Räume (ohne Wände, Flure, Schächte)",
			livingArea: "Wohnfläche (WoFlV)",
			livingAreaDescription:
				"Wohnfläche nach Wohnflächenverordnung (§ 4 WoFlV): Wohnungsgrundfläche zu 100 %, Balkone zu 25 %. Keine Abzüge für Raumhöhen.",
			floorAreaRatio: "Geschossflächenzahl (GFZ)",
			floorAreaRatioDescription:
				"Verhältnis der Brutto-Grundfläche zur Grundstücksfläche",
			areaToVolumeRatio: "Flächen-Volumen-Verhältnis (A/V)",
			areaToVolumeRatioDescription:
				"Verhältnis der Gebäudehüllfläche zum Gebäudevolumen",
			unitMixDescription: "Wohnungsanzahl nach Schlafzimmern und Bewohnern",
			layoutEfficiencyPercent: "Layout-Effizienz (%)",
			layoutEfficiencyTooltip:
				"Layout-Effizienz: Verhältnis der Netto-Nutzfläche zur Brutto-Grundfläche.\\n\\nNetto-Nutzfläche (NNF): Gesamte nutzbare Fläche innerhalb der Räume (ohne Wände, Flure, Schächte)\\n\\nBrutto-Grundfläche (BGF): Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			lotCoverageRatioPercent: "Grundflächenzahl (%)",
			lotCoverageRatioTooltip:
				"Grundstücksabdeckungsverhältnis: Prozentsatz des Grundstücks, der von Gebäuden bedeckt ist.\n\nGebäudegrundfläche: Gesamtfläche, die von Gebäuden auf Bodenhöhe bedeckt ist\n\nGrundstücksfläche: Gesamtfläche des Entwicklungsgrundstücks",
			buildingFootprintSqm: "Gebäudegrundfläche (m²)",
			buildingFootprintTooltip:
				"Gesamtfläche, die von Gebäuden auf Bodenhöhe bedeckt ist",
			grossFloorAreaSqm: "Brutto-Grundfläche (m²)",
			grossFloorAreaTooltip:
				"Gesamte Geschossfläche aller Stockwerke (inkl. Wände, Flure, Schächte)",
			floorAreaRatioPercent: "Geschossflächenzahl (%)",
			floorAreaRatioTooltip:
				"Geschossflächenzahl: Verhältnis der gesamten Gebäudegrundfläche zur Grundstücksfläche.\\n\\nBrutto-Grundfläche (BGF): Gesamte Geschossfläche aller Stockwerke\\n\\nGrundstücksfläche: Gesamtfläche des Entwicklungsgrundstücks",
			areaToVolumeRatioTooltip:
				"Verhältnis der Gebäudehüllfläche zum Gebäudevolumen",
			variantCount: "Variantenanzahl",
		},
		paragraph34Panel: {
			far: "GFZ",
			gsi: "GRZ",
			height: "Höhe",
			noData: "Keine Daten. Bitte Grundstück und Parzellen laden.",
			loadingBuildings: "Gebäude werden geladen…",
			withinLimits: "im Rahmen",
			parcels: "Parzellen",
			parcel: "Parzelle",
			referenceLabel: "Referenz",
			selectParcelHint: "Parzelle anklicken, um sie als Referenz zu verwenden.",
			pickOnMap: "Auf Karte wählen",
			pickOnMapCancel: "Abbrechen",
			pickOnMapHint:
				"Beliebige Parzelle in der Szene anklicken, um sie als Referenz zu verwenden. Zum Vorschauen darüberfahren.",
			manualReferenceInfo:
				"ist die Referenz — ihre GFZ, GRZ und Höhe bestimmen die §34-Grenzwerte und Zielvorschläge.",
			resetReference: "Auf Auto zurücksetzen",
			legendTitle: "Farblegende",
			legendInRange: "Auf oder unter Ref — im Rahmen von §34",
			legendAboveMax: "Über Ref — überschreitet Referenzparzelle",
			infoTitle: "So werden §34-Zielwerte berechnet",
			infoNeighbours:
				"Nachbarn: Kataster-Parzellen, deren Umriss höchstens 200 m vom Umriss der Grundstücksparzelle entfernt ist, bilden den Kontext.",
			infoFilter:
				"Filter: leere Parzellen (ohne Gebäude) und statistische Ausreißer bei GFZ und Höhe werden entfernt.",
			infoReference:
				"Referenzparzelle: unter den verbleibenden Nachbarn diejenige mit der höchsten GFZ. Ihre GFZ, GRZ und Höhe bilden das Ref (Obergrenze).",
			infoCompliance:
				"Das Projekt liegt im Rahmen von §34, wenn jede Kennzahl auf oder unter Ref bleibt.",
		},
		report: {
			title: "Projektbericht",
			noBuildings: "Keine Gebäude in diesem Layout",
			noBuildingsDescription:
				"Ohne Einheiten kann kein Bericht erstellt werden. Erstellen Sie zuerst ein Gebäude.",
			generating: "Bericht wird erstellt...",
			reportDate: "Berichtsdatum",
			developmentSummary: "Entwicklungsübersicht",
			basis: "Grundlage",
			basisNote:
				"Die Zahlen basieren auf dem aktiven Layout und den im Projekt festgelegten Bewertungsannahmen.",
			siteAndDesignMetrics: "Standort- und Entwurfskennzahlen",
			siteAndDesignMetricsSubtitle:
				"Was der Standort ermöglicht, was der Entwurf leistet und wie effizient die bebaute Fläche genutzt wird.",
			costAndRevenueModel: "Kosten- und Erlösmodell",
			costAndRevenueModelSubtitle:
				"Aufschlüsselung der Baukosten, Projektkosten, Finanzierung und die daraus resultierende Rendite.",
			feasibilityReport: "Machbarkeitsbericht",
			feasibilityStudy: "Machbarkeitsstudie",
			coverSubtitle:
				"Potenzialanalyse für die Entwicklung von hochwertigem Wohnraum in urbaner Lage.",
			massingTopView: "Baumassen — Draufsicht",
			estimate: "Schätzwert",
			range: "Spanne",
			perSqmLivingArea: "Je m² Wohnfläche",
			monthly: "monatlich",
			subtotalPreFinance: "Zwischensumme vor Finanzierung",
			priceSourceMarket: "Marktschätzung",
			priceSourceManual: "manuell",
			page: "Seite",
			printReport: "Bericht drucken",
			units: "Einheiten",
			share: "Anteil",
			region: "Bereich",
			area: "Fläche",
			ofSite: "Am Grundstück",
			shareOfDesignatedArea: "Anteil der ausgewiesenen Fläche",
			item: "Kostengruppe / Position",
			rate: "Ansatz / Einheit",
			amount: "Gesamt",
			unitApartment: "WE",
			unitLevel: "Ebene",
			unitYears: "J.",
			unitFootprint: "m² GF",
			disclaimer: "Haftungsausschluss & Reliance",
			disclaimerBasisTitle: "Daten- und Methodengrundlage",
			disclaimerBasis1:
				"Die vorliegende Machbarkeitsstudie wurde nach bestem Wissen und Gewissen sowie mit der üblichen Sorgfalt erstellt. Sämtliche Analysen, Bewertungen, Kennzahlen, Prognosen und Schlussfolgerungen beruhen auf den zum Zeitpunkt der Erstellung verfügbaren Informationen, insbesondere auf Unterlagen und Angaben des Auftraggebers, öffentlich zugänglichen Quellen sowie – soweit eingesetzt – auf datenbasierten Auswertungen unter Nutzung von Softwarelösungen und Methoden der Künstlichen Intelligenz (KI).",
			disclaimerBasis2:
				"Eine eigenständige, vollumfängliche Prüfung sämtlicher Grundlagen, Datenquellen und Annahmen im Sinne einer technischen, wirtschaftlichen, steuerlichen oder rechtlichen Due Diligence wurde nicht geschuldet und nicht durchgeführt. Es erfolgte ausschließlich eine sorgfältige Plausibilisierung im Rahmen einer überschlägigen, indikativ ausgerichteten Betrachtung.",
			disclaimerWarrantyTitle: "Gewährleistungsausschluss",
			disclaimerWarranty1:
				"Trotz sorgfältiger Vorgehensweise kann keine Gewähr für die Vollständigkeit, Richtigkeit und Aktualität der verwendeten Daten, der zugrunde gelegten Annahmen sowie der daraus abgeleiteten Ergebnisse übernommen werden. Dies gilt insbesondere vor dem Hintergrund, dass (i) Teile der Inhalte auf probabilistischen und algorithmischen Verfahren, einschließlich KI-gestützter Modelle, beruhen, die systemimmanenten Unsicherheiten und Fehlerquellen unterliegen, und (ii) sich Rahmenbedingungen (z. B. Markt-, Kosten-, Zins-, Rechts- oder Förderkulisse) jederzeit ändern können.",
			disclaimerWarranty2:
				"Die in dieser Machbarkeitsstudie enthaltenen Aussagen - insbesondere zu Kosten, Terminen, Erlösen, Wirtschaftlichkeit, Risiken oder Realisierbarkeit - stellen daher unverbindliche Einschätzungen zum Zeitpunkt der Erstellung dar und sind ausdrücklich nicht als Zusage, Garantie oder Beschaffenheitsvereinbarung zu verstehen.",
			disclaimerUseTitle: "Verwendungszweck & Haftung",
			disclaimerUse1:
				"Die Machbarkeitsstudie dient ausschließlich der Orientierung und Vorbereitung weiterer Überlegungen des Auftraggebers. Sie ersetzt weder eine vertiefte technische, rechtliche, steuerliche oder wirtschaftliche Fachberatung noch eine projektspezifische Detailplanung. Der Auftraggeber bleibt verpflichtet, auf Grundlage eigener Prüfung sowie gegebenenfalls ergänzender Gutachten und Beraterleistungen eigenverantwortliche Entscheidungen zu treffen. Eine unmittelbare oder mittelbare Verwendung der Ergebnisse für Investitions-, Finanzierungs-, Vergabe- oder Erwerbsentscheidungen erfolgt auf eigenes Risiko des Auftraggebers.",
			disclaimerUse2:
				"Jegliche Haftung des Auftragnehmers für die inhaltliche Richtigkeit, Vollständigkeit und Aktualität der Machbarkeitsstudie, ihrer Grundlagen und Annahmen sowie für daraus abgeleitete Entscheidungen oder unterlassene Entscheidungen des Auftraggebers oder Dritter wird - soweit gesetzlich zulässig - ausgeschlossen. Insbesondere werden Ansprüche aus sogenannter „Reliance“ (Vertrauen auf die in dieser Studie enthaltenen Inhalte), einschließlich etwaiger mittelbarer oder Folgeschäden, ausgeschlossen. Die vorstehenden Haftungsbeschränkungen gelten für sämtliche vertraglichen, vorvertraglichen und außervertraglichen Ansprüche gleichermaßen und auch zugunsten der gesetzlichen Vertreter, Mitarbeiter und sonstigen Erfüllungsgehilfen des Auftragnehmers.",
		},
		statusMessages: {
			siteSaved: "Grundstück gespeichert",
			siteNotSaved: "Grundstück wird nicht gespeichert",
			buildingSaved: "Gebäude gespeichert",
			buildingNotSaved: "Gebäude wird nicht gespeichert",
			saving: "Speichern...",
			savedToCloud: "In der Cloud gespeichert",
		},
		buildingInfo: {
			residential: "Wohngebäude",
			units: "Einheiten",
		},
		validation: {
			buildingExceedsPlot: "Gebäude überschreitet das Grundstück",
			buildingSetbacksOverlap: "Gebäudeabstände überschneiden sich",
			buildingSetbackExceedsSiteLimit:
				"Gebäudeabstand überschreitet die Abstandsgrenze des Grundstücks",
			apartmentFacesPureNorth: "Wohnungen sind rein nach Norden ausgerichtet",
			siteExceedsMaxBcr:
				"Bebauung überschreitet die maximale GRZ (Grundflächenzahl)",
			siteExceedsMaxAncillaryBcr:
				"Bebauung inkl. versiegelter Flächen überschreitet die zulässige GRZ II (Nebenanlagen)",
			coreExceedsMaxApartments: "Treppenhaus erschließt zu viele Wohnungen",
		},
		warningsPanel: {
			noWarnings: "Keine Warnungen",
			archived: "Archiviert",
			archive: "Archivieren",
			restore: "Wiederherstellen",
			showDetails: "Details anzeigen",
			hideDetails: "Details ausblenden",
			moreDetails: (count: number) => `+${count} weitere`,
			warningTypes: "Warnungstypen",
			warningsWhileEditing: "Warnungen während der Bearbeitung",
		},
		userMenu: {
			name: "Name",
			color: "Farbe",
			language: "Sprache",
			theme: "Design",
			themeSystem: "System",
			themeLight: "Hell",
			themeDark: "Dunkel",
			organisation: "Organisation",
			manageAccount: "Konto verwalten",
			manageOrganisation: "Organisation verwalten",
			apartmentSets: "Wohnungssets",
			billing: "Abrechnung",
			appAdmin: "App-Admin",
			contactUs: "Kontaktieren Sie uns",
			signOut: "Abmelden",
			signIn: "Anmelden",
			legal: "Rechtliches",
			privacyPolicy: "Datenschutzrichtlinie",
			cookiePolicy: "Cookie-Richtlinie",
			impressum: "Impressum",
			terms: "Geschäftsbedingungen",
		},
		locationPicker: {
			cannotUseThisLocation: "Dieser Standort kann nicht verwendet werden",
			useThisLocation: "Diesen Standort verwenden",
		},
		exportPanel: {
			generate: "Generieren",
			exportType: "Format",
			generateRevit: "Revit-Datei generieren",
			generateIfc: "IFC-Datei generieren",
			revitJobLabel: "Revit-Export",
			ifcJobLabel: "IFC-Export",
			generating: "Wird generiert...",
			[ExportStatus.pending]: "Ausstehend",
			[ExportStatus.inprogress]: "In Bearbeitung",
			[ExportStatus.success]: "Abgeschlossen",
			failed: "Fehlgeschlagen",
			download: "Datei herunterladen",
			downloadReport: "Bericht herunterladen",
			delete: "Löschen",
			startNewJob: "Neuen Job starten",
			rawData: "Rohdaten",
			export: "Export",
			revitFiles: "Revit-Dateien",
			uploadRevitFile: "Revit-Datei hochladen",
			uploading: "Wird hochgeladen...",
			revitFileTooLarge: "Datei überschreitet die Größenbeschränkung",
			revitFileExists:
				"Eine Datei mit diesem Namen existiert bereits — löschen Sie sie zuerst oder benennen Sie die Datei um",
			revitFileHint: "Revit 2025 Referenzprojekt",
			referenceFile: "Referenzdatei",
			defaultReferenceFile: "Standard (integriert)",
			notMapped: "(nicht zugeordnet)",
			noBuildings: "Fügen Sie mindestens ein Gebäude hinzu, um zu exportieren",
			extracting: "Wird extrahiert...",
			extractionFailed: "Extraktion fehlgeschlagen",
			mapFamilies: "Familien zuordnen",
			downloadIfc: "IFC herunterladen",
			mappingTitle: "Familien zuordnen",
			mappingDescription:
				"Wählen Sie, welche Familie beim Export für jeden Elementtyp verwendet wird",
			mappingNone: "—",
			mappingRequired: "Ordnen Sie die Familien dieser Datei vor dem Export zu",
			mappingOutOfSync:
				"BIM-Objekt-Eigenschaften geändert — aktualisieren Sie zuerst die Familienzuordnung",
			mappingSearchPlaceholder: "Familien suchen...",
			mappingNoResults: "Keine Familien gefunden",
			mappingFixedSize: "(feste Größe)",
			mappingCategoryLabels: mappingCategoryLabelsDe,
			mappingSlotLabels: mappingSlotLabelsDe,
			mappingLoading: "Familien werden geladen...",
			mappingLoadFailed:
				"Die extrahierten Familien konnten nicht geladen werden",
			mappingSaveFailed: "Die Zuordnung konnte nicht gespeichert werden",
			mappingSave: "Speichern",
			mappingSaving: "Wird gespeichert...",
			mappingCancel: "Abbrechen",
			applySizesTitle: "BIM-Objekt-Eigenschaften aktualisieren?",
			applySizesDescription:
				"Die zugeordneten Familien haben andere Größen als die aktuellen Generierungseinstellungen. Auf dieses Layout anwenden und neu generieren?",
			applySizesApply: "Anwenden & neu generieren",
			applySizesKeep: "Aktuelle Werte behalten",
			revitFileUploadFailed:
				"Das Hochladen der Datei ist fehlgeschlagen — bitte erneut versuchen",
		},
		org: {
			apartmentSets: "Wohnungssets",
			globalApartmentSet: "Vorgegeben",
			globalApartmentSetDescription:
				"Von gen.os.build bereitgestellt und in jedes neue Projekt kopiert. Fügen Sie eigene Wohnungssets hinzu, um sie anzupassen.",
			defaultApartmentSet: "Standard für neue Projekte",
			defaultApartmentSetDescription:
				"Neue Projekte starten mit diesem Wohnungsset.",
			setDefaultApartmentSet: "Als Standard für neue Projekte festlegen",
			createApartmentSet: "Neues Wohnungsset",
			createApartmentGroup: "Neue Wohnungsgruppe",
			createApartmentTemplate: "Neue Wohnungsvorlage",
			bulkUploadApartmentTemplates: "Massen-Upload",
			bulkUploadApartmentTemplatesDescription:
				"Laden Sie mehrere Wohnungsvorlagen-JSON-Dateien gleichzeitig hoch. Jede wird der in der Datei genannten Gruppe hinzugefügt; fehlt die Gruppe, wird sie angelegt.",
			bulkUploadReadyCount: (count: number) =>
				`${count} Wohnungsvorlage${count === 1 ? "" : "n"} zum Hochladen bereit`,
			bulkUploadSkippedCount: (count: number) =>
				`${count} Datei${count === 1 ? "" : "en"} übersprungen — keine gültige Wohnungsvorlage`,
			bulkUploadProgress: (uploaded: number, total: number) =>
				`${uploaded} von ${total} hochgeladen`,
			bulkUploadFailedCount: (count: number) =>
				`${count} Upload${count === 1 ? "" : "s"} fehlgeschlagen`,
			upload: "Hochladen",
			close: "Schließen",
			name: "Name",
			color: "Farbe",
			shuffleColor: "Farbe zufällig wählen",
			create: "Erstellen",
			cancel: "Abbrechen",
			remove: "Entfernen",
			update: "Aktualisieren",
			apartmentGroup: "Wohnungsgruppe",
			newApartmentGroup: "Neue Gruppe…",
			addApartmentTemplate: "Wohnungsvorlage hinzufügen",
			chooseApartmentGroup:
				"Wähle die Gruppe, zu der diese Wohnung gehören soll.",
			add: "Hinzufügen",
			removeApartmentSet: "Wohnungsset entfernen",
			removeApartmentSetDescription:
				"Dieses Wohnungsset und alle zugehörigen Gruppen und Vorlagen werden endgültig entfernt.",
			removeApartmentTemplate: "Wohnungsvorlage entfernen",
			removeApartmentTemplateDescription:
				"Diese Wohnungsvorlage wird endgültig entfernt.",
		},
		appAdmin: {
			defaultApartmentSets: "Standard-Wohnungssets",
		},
		errorMessages: {
			whoops: "Hoppla!",
			goToProjects: "Zu Projekten gehen",
			errors: {
				generic: "Etwas ist schief gelaufen.",
				[401]: "Sie sind nicht berechtigt, diese Seite anzuzeigen.",
				[403]: "Sie haben keine Berechtigung, auf diese Seite zuzugreifen.",
				[404]: "Die gesuchte Seite existiert nicht.",
				[410]: "Die gesuchte Seite wurde dauerhaft entfernt.",
				[429]:
					"Sie haben zu viele Anfragen gestellt. Bitte versuchen Sie es später erneut.",
				[500]: "Etwas ist schief gelaufen.",
				[503]: "Etwas ist schief gelaufen.",
			},
			organizationError: {
				message:
					"Sie müssen Teil einer Organisation sein, um diese App zu nutzen",
				action: "Bitte kontaktieren Sie den Administrator oder ",
				link: "fordern Sie eine Demo an",
			},
		},
	},
};
